import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  onSnapshot,
  Unsubscribe,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebaseService';
import { WorkTimeTemplate, TimeRange, WorkTimeTemplateFirestore } from '../types/workTimeTemplate';
import { checkWebAccess } from './subscriptionService';

/**
 * 근무 타임 템플릿 서비스 (읽기 전용)
 * Flutter 앱에서만 생성/수정 가능, React에서는 조회만 가능
 */
class WorkTimeTemplateService {
  /**
   * 모든 템플릿 조회
   * @param userId 사용자 ID 또는 공유가계부 ID
   * @returns 템플릿 목록
   */
  async getAllTemplates(userId: string): Promise<WorkTimeTemplate[]> {
    try {
      
      // 프리미엄/멤버 사용자 확인
      const hasAccess = await checkWebAccess(userId);
      if (!hasAccess) {
        return [];
      }
      
      const templatesRef = collection(db, `sharedBooks/${userId}/workTimeTemplates`);
      const q = query(templatesRef, orderBy('created_at', 'desc'));
      
      const snapshot = await getDocs(q);
      
      const templates = snapshot.docs.map(doc => {
        return this._fromFirestore(doc.id, doc.data() as WorkTimeTemplateFirestore);
      });
      
      
      return templates;
    } catch (error: any) {
      console.error('❌ WorkTimeTemplateService.getAllTemplates 오류:', error);
      console.error('❌ 오류 상세:', {
        message: error.message,
        code: error.code,
        name: error.name,
        stack: error.stack
      });
      return [];
    }
  }
  
  /**
   * 템플릿 ID로 조회
   * @param userId 사용자 ID 또는 공유가계부 ID
   * @param templateId 템플릿 ID
   * @returns 템플릿 또는 null
   */
  async getTemplateById(
    userId: string,
    templateId: string
  ): Promise<WorkTimeTemplate | null> {
    try {
      
      // 프리미엄/멤버 사용자 확인
      const hasAccess = await checkWebAccess(userId);
      if (!hasAccess) {
        return null;
      }
      
      const templateRef = doc(db, `sharedBooks/${userId}/workTimeTemplates/${templateId}`);
      const docSnap = await getDoc(templateRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      const template = this._fromFirestore(docSnap.id, docSnap.data() as WorkTimeTemplateFirestore);
      
      return template;
    } catch (error: any) {
      console.error('❌ WorkTimeTemplateService.getTemplateById 오류:', error);
      return null;
    }
  }
  
  /**
   * 실시간 템플릿 구독
   * @param userId 사용자 ID 또는 공유가계부 ID
   * @param callback 템플릿 목록 변경 시 호출되는 콜백
   * @returns 구독 해제 함수
   */
  subscribeTemplates(
    userId: string,
    callback: (templates: WorkTimeTemplate[]) => void
  ): () => void {
    
    // 프리미엄/멤버 사용자 확인 (비동기)
    checkWebAccess(userId).then(hasAccess => {
      if (!hasAccess) {
        callback([]);
        return;
      }
      
      const templatesRef = collection(db, `sharedBooks/${userId}/workTimeTemplates`);
      const q = query(templatesRef, orderBy('created_at', 'desc'));
      
      const unsubscribe: Unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const templates = snapshot.docs.map(doc => {
            return this._fromFirestore(doc.id, doc.data() as WorkTimeTemplateFirestore);
          });
          
          
          callback(templates);
        },
        (error) => {
          console.error('❌ 템플릿 구독 오류:', error);
          callback([]);
        }
      );
      
      return unsubscribe;
    }).catch(error => {
      console.error('❌ 프리미엄 확인 실패:', error);
      callback([]);
    });
    
    // 구독 해제 함수 반환 (실제로는 onSnapshot의 반환값을 저장해야 함)
    return () => {
    };
  }
  
  /**
   * Firestore 데이터를 모델로 변환
   */
  private _fromFirestore(id: string, data: WorkTimeTemplateFirestore): WorkTimeTemplate {
    return {
      id,
      name: data.name,
      timeRanges: this._parseTimeRanges(data.time_ranges),
      breakTime: data.break_time,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
  
  /**
   * time_ranges JSON 문자열을 TimeRange[]로 파싱
   */
  private _parseTimeRanges(timeRangesJson: string): TimeRange[] {
    try {
      const ranges = JSON.parse(timeRangesJson);
      const now = new Date();
      
      return ranges.map((range: { start: string; end: string }) => {
        const [startHour, startMin] = range.start.split(':').map(Number);
        const [endHour, endMin] = range.end.split(':').map(Number);
        
        const start = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          startHour,
          startMin
        );
        
        let end = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          endHour,
          endMin
        );
        
        // 다음날로 넘어가는 경우
        if (end < start) {
          end = new Date(end);
          end.setDate(end.getDate() + 1);
        }
        
        return { start, end };
      });
    } catch (error) {
      console.error('❌ time_ranges 파싱 오류:', error);
      return [];
    }
  }
}

export const workTimeTemplateService = new WorkTimeTemplateService();


